import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StudentPaidAvailability.css';

const StudentPaidAvailability = ({ assignedTeacher, studentId }) => {
  const [teacherAvailability, setTeacherAvailability] = useState([]);
  const [allStudentSlots, setAllStudentSlots] = useState([]);
  const [currentStudentSlots, setCurrentStudentSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Days and time slots definitions...
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const slotToTimeView = {
    8: '8:00 AM',
    9: '9:00 AM',
    10: '10:00 AM',
    11: '11:00 AM',
    12: '12:00 PM',
    13: '1:00 PM',
    14: '2:30 PM',
    15: '3:30 PM',
    16: '4:30 PM',
    17: '5:30 PM',
    18: '6:30 PM',
    19: '7:30 PM',
    20: '8:30 PM',
    21: '9:30 PM',
    22: '10:30 PM'
  };

  // Fetch data
  useEffect(() => {
    const fetchAvailabilityData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacher/paidavailability/${assignedTeacher}`
        );
        
        setTeacherAvailability(response.data.availability || []);
        const allSlots = response.data.students.map(slot => `${slot.DayoftheWeek}-${slot.Slot}`);
        setAllStudentSlots(allSlots);
        
        const currentStudentSelectedSlots = response.data.students
          .filter(slot => slot.IdUser === studentId)
          .map(slot => `${slot.DayoftheWeek}-${slot.Slot}`);
        setCurrentStudentSlots(currentStudentSelectedSlots);

      } catch (error) {
        console.error('Error fetching availability:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (assignedTeacher) {
      fetchAvailabilityData();
    }
  }, [assignedTeacher, studentId]);

  // Helper functions - defined before they're used
  const checkTeacherAvailability = (dayIndex, slot) => {
    return teacherAvailability.some(
      item => item.DayoftheWeek === dayIndex && item.Slot === slot && item.Checkteacher === 1
    );
  };

  const checkSlotTaken = (dayIndex, slot) => {
    return allStudentSlots.includes(`${dayIndex}-${slot}`);
  };

  const checkCurrentStudentSelected = (dayIndex, slot) => {
    return currentStudentSlots.includes(`${dayIndex}-${slot}`);
  };

  const checkSlotAvailable = (dayIndex, slot) => {
    return checkTeacherAvailability(dayIndex, slot) && 
           (!checkSlotTaken(dayIndex, slot) || checkCurrentStudentSelected(dayIndex, slot));
  };



  const showNotification = (message, color) => {
    setNotification({ message, color });
    setTimeout(() => setNotification(null), 3000);
  };

  if (isLoading) {
    return (
      <div className="pa-loading-container">
        <div className="pa-loading-spinner"></div>
        <p>Loading Availability...</p>
      </div>
    );
  }

  return (
    <div className="pa-container">
      {notification && (
        <div className="pa-notification" style={{ backgroundColor: notification.color }}>
          {notification.message}
        </div>
      )}

      <div className="pa-table">
        <div className="pa-table-header text-center">
          <h3>Paid Availability</h3>
          <p>Choose your slots for the week sessions.</p>
          <p className="availability-info">
            <span className="available-indicator"></span> Available
            <span className="selected-indicator"></span> Your Selection
            <span className="taken-indicator"></span> Taken by Others
            <span className="unavailable-indicator"></span> Not Available
          </p>
        </div>

        <div className="pa-table-body">
          <div className="pa-time-column">
            <div className="pa-time-header">Time</div>
            {Object.entries(slotToTimeView).map(([slot, time]) => (
              <div key={slot} className="pa-time-slot-label">{time}</div>
            ))}
          </div>

          <div className="pa-days-container">
            <div className="pa-days-header">
              {daysOfWeek.map((day, index) => (
                <div key={index} className="pa-day-header">
                  {shortDays[index]}
                </div>
              ))}
            </div>

            <div className="pa-grid">
              {Object.keys(slotToTimeView).map(slot => {
                const slotNum = parseInt(slot);
                return (
                  <div key={slot} className="pa-time-row">
                    {daysOfWeek.map((day, index) => {
                      const dayIndex = index + 1;
                      const isAvailable = checkSlotAvailable(dayIndex, slotNum);
                      const isSelected = checkCurrentStudentSelected(dayIndex, slotNum);
                      const isTakenByOthers = checkSlotTaken(dayIndex, slotNum) && !isSelected;
                      const isTeacherAvailable = checkTeacherAvailability(dayIndex, slotNum);

                      return (
                        <div key={`${dayIndex}-${slotNum}`} className="pa-time-slot">
                          {isTeacherAvailable ? (
                            isTakenByOthers ? (
                              <div className="pa-student-slot taken">
                                <div className="pa-student-name">
                                  Taken
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`pa-availability-slot ${isSelected ? 'pa-checked1 active' : ' '}`}
                                
                              >
                                <div className="pa-student-slot pa-available-text">
                                  <div className="pa-student-name">
                                    {isSelected ? 'Your Slot' : 'Available'}
                                  </div>
                                  <div className="pa-sessions-remaining">
                                    {slotToTimeView[slot]}
                                  </div>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="pa-student-slot unavailable">
                              <div className="pa-student-name">
                                Not Available
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentPaidAvailability;